import {
  LoginCredentials,
  RegisterCredentials,
  AuthResponse,
  User,
  AUTH_STORAGE_KEYS,
} from '@/types'

import apiClient from './api'

class AuthService {
  /**
   * Extract and store auth tokens from response
   */
  private storeAuthTokens(response: AuthResponse): void {
    if (response.access_token) {
      localStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, response.access_token)
    }
    if (response.refresh_token) {
      localStorage.setItem(
        AUTH_STORAGE_KEYS.REFRESH_TOKEN,
        response.refresh_token
      )
    }
  }

  /**
   * Clear all auth data from localStorage
   */
  private clearAuthData(): void {
    localStorage.removeItem(AUTH_STORAGE_KEYS.USER)
    localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN)
    localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
  }

  /**
   * Get stored refresh token
   */
  private getRefreshToken(): Nullable<string> {
    return localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN)
  }

  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/auth/login', {
        email: credentials.email,
        password: credentials.password,
        remember_me: credentials.rememberMe || false,
      })

      // Store auth tokens
      this.storeAuthTokens(response.data)

      // Get and store user profile after successful login
      try {
        await this.getCurrentUser()
      } catch (userError) {
        console.error('Failed to fetch user profile:', userError)
        // Continue even if fetching user profile fails
      }

      return response.data as AuthResponse
    } catch (error) {
      throw this.handleAuthError(error)
    }
  }

  /**
   * Register new user
   */
  async register(_credentials: RegisterCredentials): Promise<AuthResponse> {
    try {
      // For demo purposes, simulate registration
      // const response = await apiClient.post('/auth/register', credentials)

      // Mock response for demo - should be replaced with real API call
      const mockAuthResponse: AuthResponse = {
        access_token: 'mock_jwt_token_' + Date.now(),
        refresh_token: 'mock_refresh_token_' + Date.now(),
        token_type: 'bearer',
        expires_in: 3600,
      }

      // Store auth tokens
      this.storeAuthTokens(mockAuthResponse)

      return mockAuthResponse
    } catch (error) {
      throw this.handleAuthError(error)
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      const refreshToken = this.getRefreshToken()
      await apiClient.post('/auth/logout', {
        refresh_token: refreshToken,
      })
      this.clearAuthData()
    } catch (error) {
      console.error('Logout error:', error)
      this.clearAuthData()
    }
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<string> {
    try {
      const refreshToken = this.getRefreshToken()
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      const response = await apiClient.post('/auth/refresh', {
        refresh_token: refreshToken,
      })

      this.storeAuthTokens(response.data)

      return response.data.access_token
    } catch (error) {
      this.clearAuthData()
      throw this.handleAuthError(error)
    }
  }

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User> {
    try {
      const response = await apiClient.get('/users/me')
      const user = response.data as User

      // Store user data in localStorage using AUTH_STORAGE_KEYS
      if (user) {
        localStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(user))
      }

      return user
    } catch (error) {
      console.error('getCurrentUser - Error:', error)
      throw this.handleAuthError(error)
    }
  }

  /**
   * Check if user is authenticated by verifying token
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN)
  }

  /**
   * Get stored user data
   */
  getStoredUser(): Nullable<User> {
    try {
      const user = localStorage.getItem(AUTH_STORAGE_KEYS.USER)
      return user ? (JSON.parse(user) as User) : null
    } catch {
      return null
    }
  }

  /**
   * Get stored auth token
   */
  getStoredToken(): Nullable<string> {
    return localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN)
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: unknown): Error {
    // Handle axios errors
    if (typeof error === 'object' && error !== null && 'response' in error) {
      const axiosError = error as {
        response?: {
          data?: {
            message?: string
            detail?: string
            errors?: Record<string, string[]>
          }
          status?: number
        }
      }

      // Handle new API error format with 'detail' field
      if (axiosError.response?.data?.detail) {
        return new Error(axiosError.response.data.detail)
      }

      if (axiosError.response?.data?.message) {
        return new Error(axiosError.response.data.message)
      }

      if (axiosError.response?.status === 401) {
        return new Error('Invalid credentials')
      }

      if (axiosError.response?.status === 422) {
        const errors = axiosError.response.data?.errors
        if (errors) {
          const errorMessages = Object.values(errors).flat().join(', ')
          return new Error(errorMessages)
        }
        return new Error('Validation failed')
      }
    }

    if (error instanceof Error) {
      return new Error(error.message)
    }

    return new Error('An unexpected error occurred')
  }
}

// Export singleton instance
export const authService = new AuthService()
export default authService
